/**
 * Add a flat (multi-society). A logged-in resident searches for a registered
 * society, picks their flat, declares owner/tenant/family, and submits a join
 * request (POST /api/me/flats → PENDING membership) that the society admin then
 * approves. Distinct from onboarding (which creates the ACCOUNT); this attaches
 * another society to an existing account.
 *
 * Three inline steps in one screen: search society → pick flat → confirm.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';

import { space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Chip, EmptyState, Field, Hairline, Input, Screen, Skeleton, TopBar } from '@/ui';

type Soc = { id: number; name: string; city?: string };
type Flat = { configId: number; code: string; bhk?: string; floor?: number; claimed: boolean };

const RESIDENT_TYPES = ['OWNER', 'TENANT', 'FAMILY'] as const;
type ResType = (typeof RESIDENT_TYPES)[number];

function useDebounced<T>(v: T, ms: number): T {
  const [d, setD] = React.useState(v);
  React.useEffect(() => { const id = setTimeout(() => setD(v), ms); return () => clearTimeout(id); }, [v, ms]);
  return d;
}

export default function AddFlatScreen() {
  const { colors } = useTheme();
  const { t } = useT();

  const [q, setQ] = React.useState('');
  const dq = useDebounced(q, 300);
  const [socs, setSocs] = React.useState<Soc[]>([]);
  const [searching, setSearching] = React.useState(false);

  const [soc, setSoc] = React.useState<Soc | null>(null);
  const [flats, setFlats] = React.useState<Flat[]>([]);
  const [flatsLoading, setFlatsLoading] = React.useState(false);
  const [flat, setFlat] = React.useState<Flat | null>(null);
  const [rtype, setRtype] = React.useState<ResType>('OWNER');

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [done, setDone] = React.useState(false);

  // Step 1 — society search. All setState is deferred into a microtask (matching
  // onboarding/society.tsx) so nothing runs synchronously in the effect body.
  React.useEffect(() => {
    let cancelled = false;
    void Promise.resolve().then(async () => {
      if (soc || !dq.trim()) { if (!cancelled) setSocs([]); return; }
      if (!cancelled) setSearching(true);
      try {
        const b = await api.get<{ societies?: Soc[]; items?: Soc[] }>(`/api/societies?q=${encodeURIComponent(dq.trim())}`);
        if (!cancelled) setSocs(b.societies ?? b.items ?? []);
      } catch {
        if (!cancelled) setSocs([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    });
    return () => { cancelled = true; };
  }, [dq, soc]);

  // Step 2 — load flats once a society is picked
  const pickSociety = (s: Soc) => {
    setSoc(s); setFlat(null); setFlatsLoading(true); setError('');
    api.get<{ flats: Flat[] }>(`/api/societies/${s.id}/flats`)
      .then((b) => setFlats(Array.isArray(b.flats) ? b.flats : []))
      .catch(() => setFlats([]))
      .finally(() => setFlatsLoading(false));
  };

  const submit = async () => {
    if (!soc || !flat || busy) return;
    setBusy(true); setError('');
    try {
      await api.post('/api/me/flats', { societyId: soc.id, flatConfigId: flat.configId, residentType: rtype });
      setDone(true);
    } catch (e) {
      setError((e as { error?: string })?.error ?? t('af_err'));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Screen scroll={false}>
        <TopBar title="" onBack={() => router.replace('/account/manage-flats')} bordered={false} />
        <EmptyState
          icon={<Icon name="check" size={40} color={colors.success500} />}
          title={t('af_done_title')}
          body={t('af_done_body', { society: soc?.name ?? '' })}
          cta={t('af_done_cta')}
          onCta={() => router.replace('/account/manage-flats')}
        />
      </Screen>
    );
  }

  return (
    <Screen scroll={false}>
      <TopBar title={t('af_title')} onBack={() => router.back()} />
      <Screen padded>
        {/* Step 1 — pick society */}
        {!soc ? (
          <>
            <Field label={t('af_find_society')}>
              <Input value={q} onChangeText={setQ} placeholder={t('af_search_ph')} autoCapitalize="words" />
            </Field>
            {searching ? (
              <View style={{ gap: space.s2, marginTop: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={52} />)}</View>
            ) : socs.length > 0 ? (
              <View style={{ marginTop: space.s2 }}>
                {socs.map((s, i) => (
                  <View key={s.id}>
                    {i > 0 ? <Hairline /> : null}
                    <Pressable
                      accessibilityRole="button"
                      onPress={() => pickSociety(s)}
                      style={({ pressed }) => ({
                        paddingVertical: 14, paddingHorizontal: space.s2,
                        backgroundColor: pressed ? colors.canvas1 : 'transparent',
                      })}
                    >
                      <AppText variant="bodyMd" weight="500">{s.name}</AppText>
                      {s.city ? <AppText variant="bodySm" tone="tertiary">{s.city}</AppText> : null}
                    </Pressable>
                  </View>
                ))}
              </View>
            ) : dq.trim() ? (
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s3 }}>{t('af_no_match')}</AppText>
            ) : null}
          </>
        ) : (
          <>
            {/* chosen society, editable */}
            <Pressable onPress={() => { setSoc(null); setFlat(null); }} style={{ marginBottom: space.s4 }}>
              <AppText variant="bodySm" tone="brand" weight="500">‹ {t('af_change_society')}</AppText>
              <AppText variant="headingSm" weight="600" style={{ marginTop: 2 }}>{soc.name}</AppText>
            </Pressable>

            {/* Step 2 — pick flat */}
            <AppText variant="label" tone="tertiary" style={{ marginBottom: space.s2 }}>{t('af_pick_flat')}</AppText>
            {flatsLoading ? (
              <View style={{ gap: space.s2 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={44} />)}</View>
            ) : flats.length === 0 ? (
              <AppText variant="bodySm" tone="tertiary">{t('af_no_flats')}</AppText>
            ) : (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
                {flats.map((f) => (
                  <Chip
                    key={f.configId}
                    label={f.code}
                    active={flat?.configId === f.configId}
                    disabled={f.claimed}
                    onPress={() => setFlat(f)}
                  />
                ))}
              </View>
            )}

            {/* Step 3 — resident type + submit */}
            {flat ? (
              <View style={{ marginTop: space.s5 }}>
                <AppText variant="label" tone="tertiary" style={{ marginBottom: space.s2 }}>{t('af_you_are')}</AppText>
                <View style={{ flexDirection: 'row', gap: space.s2 }}>
                  {RESIDENT_TYPES.map((r) => (
                    <Chip key={r} label={t(`af_rt_${r.toLowerCase()}` as 'af_rt_owner')} active={rtype === r} onPress={() => setRtype(r)} />
                  ))}
                </View>
                {error ? <AppText variant="bodySm" style={{ color: colors.danger500, marginTop: space.s3 }}>{error}</AppText> : null}
                <Button
                  title={t('af_submit')}
                  size="lg"
                  loading={busy}
                  onPress={() => void submit()}
                  style={{ marginTop: space.s5 }}
                />
                <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s3, textAlign: 'center' }}>
                  {t('af_admin_note')}
                </AppText>
              </View>
            ) : null}
          </>
        )}
      </Screen>
    </Screen>
  );
}
